import { Info } from 'lucide-react';
import { cn } from '~/shadcn/lib/utils';
import { Input } from '~/shadcn/components/ui/Input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shadcn/components/ui/tooltip';

/**
 * A form input component with a label, tooltip, and error message.
 * @param {object} props
 * @param {string} props.label - The label for the input.
 * @param {boolean} props.required - Whether the input is required.
 * @param {string} props.tooltip - The tooltip for the input.
 * @param {string} props.error - The error message for the input.
 * @param {object} props.props - The props for the input.
 * @returns {JSX.Element}
 */
function FormInput({ label, required, tooltip, error, onChange, ...props }) {
    const isNumeric = props.type === 'number';

    const handleChange = (e) => {
        // If the input is meant to be numeric, validate it.
        // Otherwise, just pass the event through.
        if (isNumeric) {
            const { value } = e.target;
            const regex = /^\d*$/;
            if (value === '' || regex.test(value)) {
                onChange(e);
            }
        } else {
            onChange(e);
        }
    };

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600 flex items-center">
                    {label}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {tooltip && (
                    <TooltipProvider>
                        <Tooltip modal={true}>
                            <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent className="z-[10000]">
                                <p>{tooltip}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
            <Input
                {...props}
                type={isNumeric ? 'text' : props.type}
                inputMode={isNumeric ? 'numeric' : undefined}
                pattern={isNumeric ? '[0-9]*' : undefined}
                onChange={handleChange}
                className={cn(
                    'border-0 border-b rounded-none px-1 h-auto py-1 bg-transparent text-sm',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500',
                    error ? 'border-red-500' : 'border-gray-300'
                )}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
    );
}

export default FormInput;
